import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { tap } from 'rxjs';

import { AssignmentService } from '../../core/assignment/assignment.service';
import { AssignmentKind, AssignmentState } from '../../core/assignment/assignment.types';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog';
import { IconComponent } from '../icon/icon';
import { readError } from '../../core/http/read-error';
import { AssignmentHistoryDialogComponent } from './history-dialog';
import { AssignmentMoveDialogComponent, MoveDialogData } from './move-dialog';

/**
 * Who at CapX is dealing with an application — for the three Ops detail pages.
 *
 * Shows the current person; clicking it moves the application, and the move
 * dialog links to the history of moves (append-only on the server: every move
 * is a new row). Without the right to move, clicking it opens the history
 * directly. Opening an application nobody is dealing
 * with, while it is with CapX, asks *Deal with this application?* **once per
 * person per application** (DOC-89-A §6 finding E; FLOW-401): *Not now* — or
 * closing the question any other way — is recorded on the server for that
 * person, never in the browser, and leaves a **Deal with this** button. Any
 * move goes through the server's rules (`assignment.policy`) — the screen
 * decides nothing.
 */
@Component({
  selector: 'app-assignment-panel',
  imports: [NgTemplateOutlet, MatButtonModule, MatTooltipModule, IconComponent],
  templateUrl: './assignment-panel.html',
  styleUrl: './assignment-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentPanelComponent implements OnInit {
  readonly kind = input.required<AssignmentKind>();
  readonly shortId = input.required<string>();

  private readonly service = inject(AssignmentService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly state = signal<AssignmentState | null>(null);
  protected readonly loadFailed = signal(false);
  protected readonly taking = signal(false);
  protected readonly error = signal<string | null>(null);

  ngOnInit(): void {
    this.service
      .get(this.kind(), this.shortId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.state.set(state);
          if (state.offer_take) this.askToTake();
        },
        error: () => this.loadFailed.set(true),
      });
  }

  /** The pop-up on opening an application nobody is dealing with. */
  private askToTake(): void {
    const data: ConfirmDialogData = {
      title: 'Deal with this application?',
      message: 'Nobody is dealing with this application yet.',
      confirmLabel: "Yes, I'll deal with it",
      cancelLabel: 'Not now',
      confirm: () => this.takeRequest().pipe(tap((state) => this.state.set(state))),
      errorMessage: (err: unknown) => readError(err),
    };
    this.dialog
      .open(ConfirmDialogComponent, { data, width: '440px' })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((taken?: boolean) => {
        if (!taken) this.notNow();
      });
  }

  /** Not taken: remember that for this person on this record, so it is not asked again. */
  private notNow(): void {
    this.service
      .notNow(this.kind(), this.shortId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => this.state.set(state),
        error: () => undefined, // the page is unaffected; it is asked again next time
      });
  }

  protected take(): void {
    if (this.taking()) return;
    this.taking.set(true);
    this.error.set(null);
    this.takeRequest().subscribe({
      next: (state) => {
        this.taking.set(false);
        this.state.set(state);
      },
      error: (err: unknown) => {
        this.taking.set(false);
        this.error.set(readError(err));
      },
    });
  }

  protected openHistory(): void {
    const history = this.state()?.history ?? [];
    this.dialog.open(AssignmentHistoryDialogComponent, { data: history });
  }

  protected openMove(): void {
    const state = this.state();
    if (!state) return;
    const data: MoveDialogData = { kind: this.kind(), shortId: this.shortId(), state };
    this.dialog
      .open(AssignmentMoveDialogComponent, { data })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((next?: AssignmentState) => {
        if (next) this.state.set(next);
      });
  }

  /** Take it for the caller, from nobody — a colleague's take since the page
   *  loaded is refused (409) rather than overwritten. */
  private takeRequest() {
    return this.service.move(this.kind(), this.shortId(), {
      take: true,
      expected_from_user_id: null,
    });
  }
}
